import { API_BASE as API_BASE_URL } from '../config';
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user_data';

/**
 * Сохранить токены в localStorage
 */
export const saveTokens = (access, refresh) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, access);
  if (refresh) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  }
};

/**
 * Получить access токен
 */
export const getAccessToken = () => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

/**
 * Получить refresh токен
 */
export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Сохранить данные пользователя
 */
export const saveUser = (userData) => {
  localStorage.setItem(USER_KEY, JSON.stringify(userData));
};

/**
 * Получить данные пользователя
 */
export const getUser = () => {
  const userData = localStorage.getItem(USER_KEY);
  return userData ? JSON.parse(userData) : null;
};

/**
 * Очистить все данные авторизации
 */
export const clearAuth = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

/**
 * Проверить, авторизован ли пользователь
 */
export const isAuthenticated = () => {
  return !!getAccessToken();
};

/**
 * Ответ сервера с токенами: сохраняем их и данные пользователя.
 */
const acceptTokens = (data) => {
  saveTokens(data.access, data.refresh);
  if (data.user) {
    saveUser(data.user);
  }
};

const postJson = async (path, body) => {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...CLIENT_HEADERS },
    body: JSON.stringify(body),
  });
  let data = {};
  try {
    data = await response.json();
  } catch (e) {
    data = {};
  }
  return { response, data };
};

/**
 * Разбор ответа шага входа или регистрации:
 * - токены — вход выполнен;
 * - 202 с challenge_id — на почту отправлен код, его нужно подтвердить.
 */
const authStepResult = (response, data, fallbackError) => {
  if (response.status === 202 && data.code_required) {
    return { success: true, codeRequired: true, challenge: data };
  }
  if (response.ok && data.access) {
    acceptTokens(data);
    return { success: true, data };
  }
  return {
    success: false,
    status: response.status,
    error: data.message || fallbackError,
    details: data.details,
    attemptsLeft: data.attempts_left,
    resendIn: data.resend_in,
  };
};

const connectionError = () => ({ success: false, error: 'Ошибка подключения к серверу' });

/**
 * Авторизация пользователя. Браузерной версии сервер отвечает не токенами,
 * а кодом на почту — тогда в ответе codeRequired и challenge.
 */
export const login = async (username, password) => {
  try {
    const { response, data } = await postJson('/auth/login/', { username, password });
    return authStepResult(response, data, 'Ошибка авторизации');
  } catch (error) {
    return connectionError();
  }
};

/**
 * Вход по коду из письма, без пароля: код на почту. Ответ одинаковый, есть
 * такая почта в системе или нет, — дальше всегда шаг с кодом (purpose 'login').
 */
export const requestLoginCode = async (username) => {
  try {
    const { response, data } = await postJson('/auth/login/code/', { username });
    return authStepResult(response, data, 'Не удалось отправить код');
  } catch (error) {
    return connectionError();
  }
};

/**
 * Подтвердить вход или регистрацию кодом из письма.
 * purpose — 'login' или 'register'.
 */
const CONFIRM_PATHS = {
  login: '/auth/login/confirm/',
  register: '/auth/register/confirm/',
  reset: '/auth/password/reset/confirm/',
};

export const confirmCode = async (purpose, challengeId, code) => {
  const path = CONFIRM_PATHS[purpose] || CONFIRM_PATHS.login;
  try {
    const { response, data } = await postJson(path, { challenge_id: challengeId, code });
    // Сброс пароля: верный код даёт не токены, а разрешение задать новый пароль.
    if (purpose === 'reset' && response.ok && data.reset_token) {
      return { success: true, data };
    }
    return authStepResult(response, data, 'Не удалось проверить код');
  } catch (error) {
    return connectionError();
  }
};

/**
 * Сброс пароля, шаг 1: код на почту. Ответ одинаковый, есть такая почта в
 * системе или нет, — дальше всегда шаг с кодом.
 */
export const requestPasswordReset = async (username) => {
  try {
    const { response, data } = await postJson('/auth/password/reset/', { username });
    return authStepResult(response, data, 'Не удалось отправить код');
  } catch (error) {
    return connectionError();
  }
};

/**
 * Сброс пароля, шаг 3: новый пароль по разрешению из шага с кодом. При успехе
 * сразу входим — сервер возвращает токены.
 */
export const completePasswordReset = async (resetToken, password) => {
  try {
    const { response, data } = await postJson('/auth/password/reset/complete/', {
      reset_token: resetToken,
      password,
    });
    return authStepResult(response, data, 'Не удалось сменить пароль');
  } catch (error) {
    return connectionError();
  }
};

/**
 * Отправить код ещё раз (не чаще раза в минуту).
 */
export const resendCode = async (challengeId) => {
  try {
    const { response, data } = await postJson('/auth/code/resend/', { challenge_id: challengeId });
    if (response.ok) {
      return { success: true, challenge: data };
    }
    return {
      success: false,
      status: response.status,
      error: data.message || 'Не удалось отправить код',
      resendIn: data.resend_in,
    };
  } catch (error) {
    return connectionError();
  }
};

/**
 * Обновить access токен
 */
export const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return { success: false, error: 'Нет refresh токена' };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh: refreshToken,
      }),
    });

    const data = await response.json();

    if (response.ok) {
      // Сервер выдаёт новую пару: токен обновления продлевается при каждом обновлении.
      saveTokens(data.access, data.refresh || null);
      return { success: true, accessToken: data.access };
    } else {
      // Refresh токен невалидный, очищаем авторизацию
      clearAuth();
      return { success: false, error: 'Токен истек' };
    }
  } catch (error) {
    return {
      success: false,
      error: 'Ошибка подключения к серверу',
    };
  }
};

// Один общий запрос обновления на все одновременные 401: иначе десяток запросов
// журнала обновлял бы токен десять раз, и прежние refresh перестали бы совпадать.
let refreshInFlight = null;

const isApiRequest = (url) => typeof url === 'string' && url.startsWith(API_BASE_URL);
// Эти запросы сами работают с токенами — их ответ 401 повторять не нужно.
const AUTH_FLOW_PATHS = /\/auth\/(login|refresh|register|code|password)/;

/**
 * Токен доступа живёт 60 минут. Перехватчик fetch: если запрос к API с токеном
 * получил 401, один раз обновляем токены и повторяем запрос. Не вышло — сессия
 * закончилась: очищаем вход и показываем форму входа.
 */
export const installAuthRefresh = () => {
  if (typeof window === 'undefined' || window.__glpmAuthRefreshInstalled) return;
  window.__glpmAuthRefreshInstalled = true;
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const response = await originalFetch(input, init);
    if (response.status !== 401) return response;

    const url = typeof input === 'string' ? input : (input && input.url) || '';
    if (!isApiRequest(url) || AUTH_FLOW_PATHS.test(url)) return response;
    const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
    if (!headers.get('Authorization')) return response;

    refreshInFlight = refreshInFlight || refreshAccessToken().finally(() => { refreshInFlight = null; });
    const refreshed = await refreshInFlight;
    if (!refreshed.success) {
      clearAuth();
      window.dispatchEvent(new Event('glpm:session-expired'));
      return response;
    }
    headers.set('Authorization', `Bearer ${getAccessToken()}`);
    return originalFetch(input, { ...init, headers });
  };
};

/**
 * Заголовок, которым браузерная версия помечает себя. По нему сервер отличает
 * карточки, заведённые в вебе, от заведённых в мобильном приложении: оно
 * представляется своим User-Agent «GLPM/31 (Android 16; ...)».
 */
export const CLIENT_HEADERS = { 'X-GLPM-Client': 'web' };

/**
 * Получить заголовки для авторизованных запросов
 */
export const getAuthHeaders = () => {
  const token = getAccessToken();
  if (!token) {
    return { ...CLIENT_HEADERS };
  }
  return {
    ...CLIENT_HEADERS,
    'Authorization': `Bearer ${token}`,
  };
};

/**
 * Список филиалов для регистрации (публичный)
 */
export const fetchBranches = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/branches/`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error('Ошибка загрузки списка филиалов');
    }
    return await response.json();
  } catch (error) {
    return [];
  }
};

/**
 * Регистрация пользователя (тестовая работа с системой). Аккаунт создаётся
 * после кода из письма — тогда в ответе codeRequired и challenge.
 */
export const register = async (username, password, branchId, fullName, phone) => {
  try {
    const { response, data } = await postJson('/auth/register/', {
      username: username.trim(),
      password,
      branch_id: branchId,
      full_name: (fullName || '').trim(),
      phone: (phone || '').trim(),
    });
    return authStepResult(response, data, 'Ошибка регистрации');
  } catch (error) {
    return connectionError();
  }
};

/**
 * Получить данные текущего пользователя через /me
 */
export const fetchMe = async () => {
  const token = getAccessToken();
  if (!token) {
    return { success: false, error: 'Нет токена авторизации' };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/me/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });

    const data = await response.json();

    if (response.ok) {
      // Сохраняем обновленные данные пользователя
      saveUser(data);
      return { success: true, user: data };
    } else {
      // Если токен невалидный, пытаемся обновить его
      if (response.status === 401) {
        const refreshResult = await refreshAccessToken();
        if (refreshResult.success) {
          // Повторяем запрос с новым токеном
          return await fetchMe();
        } else {
          // Не удалось обновить токен, очищаем авторизацию
          clearAuth();
          return { success: false, error: 'Токен истек' };
        }
      }
      return {
        success: false,
        error: data.message || 'Ошибка получения данных пользователя',
      };
    }
  } catch (error) {
    return {
      success: false,
      error: 'Ошибка подключения к серверу',
    };
  }
};
